/* ============================================================
   Teak Tale cart drawer
   Server-rendered through the Section Rendering API: every cart
   mutation asks Shopify for the fresh `cart-drawer` section in the
   same request, so there is one round trip and no client templates.
   Loaded with `defer` before theme.js, which instantiates it.
   ============================================================ */
(() => {
  'use strict';
  const SECTION = 'cart-drawer';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const pad = n => String(n).padStart(2, '0');
  const TIMER_KEY = 'tt_cd_timer_end';
  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  };

  class TTCartDrawer {
    constructor() {
      this.el = $('#CartDrawer');
      if (!this.el) return;
      this.recCache = new Map();
      this.tick = null;
      this.busy = false;
      this._bind();
      this._afterRender();
    }

    /* ── Events (all delegated so re-rendered markup needs no rebinding) ── */
    _bind() {
      document.addEventListener('click', e => {
        const opener = e.target.closest('[data-open-cart]');
        if (opener) { e.preventDefault(); this.open(); }
      });
      document.addEventListener('tt:backdrop:click', () => this.close());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('tt:cart:updated', e => {
        if (!(e.detail && e.detail._fromDrawer)) this.refresh();
      });

      // Theme editor: re-grab the node after live re-renders and open for preview
      document.addEventListener('shopify:section:load', e => {
        if (!e.target.querySelector('#CartDrawer')) return;
        this.el = $('#CartDrawer');
        this._bindDrawer();
        this.el.classList.add('is-open');
        this.el.setAttribute('aria-hidden', 'false'); this.el.inert = false;
        this._afterRender();
      });
      document.addEventListener('shopify:section:select', e => { if (e.target.querySelector('#CartDrawer')) this.open(); });
      document.addEventListener('shopify:section:deselect', e => { if (e.target.querySelector('#CartDrawer')) this.close(); });
      this._bindDrawer();
    }

    _bindDrawer() {
      this.el.addEventListener('click', e => {
        const t = e.target;
        if (t.closest('[data-close-cart]')) { e.preventDefault(); this.close(); return; }

        const qty = t.closest('[data-cd-qty]');
        if (qty && !qty.disabled) {
          e.preventDefault();
          this._change(qty.dataset.key, parseInt(qty.dataset.cdQty, 10));
          return;
        }
        const tab = t.closest('[data-cd-tab]');
        if (tab) { this._switchTab(tab); return; }

        const toggle = t.closest('[data-cd-toggle]');
        if (toggle) {
          const target = document.getElementById(toggle.dataset.cdToggle);
          if (target) {
            target.hidden = !target.hidden;
            toggle.setAttribute('aria-expanded', String(!target.hidden));
          }
          return;
        }
        const noteBtn = t.closest('[data-cd-note-toggle]');
        if (noteBtn) {
          const body = noteBtn.nextElementSibling;
          body.hidden = !body.hidden;
          noteBtn.setAttribute('aria-expanded', String(!body.hidden));
          if (!body.hidden) $('textarea', body)?.focus();
          return;
        }
        const apply = t.closest('[data-cd-apply-code]');
        if (apply) { this._applyCode(apply.dataset.cdApplyCode); return; }

        const rm = t.closest('[data-cd-remove-code]');
        if (rm) { this._removeCode(rm.dataset.cdRemoveCode); return; }

        const checkout = t.closest('[data-cd-checkout]');
        if (checkout) {
          const terms = $('[data-cd-terms]', this.el);
          if (terms && !terms.checked) {
            e.preventDefault();
            const lbl = terms.closest('.cd__terms');
            lbl?.classList.remove('is-shake'); void lbl?.offsetWidth; lbl?.classList.add('is-shake');
          }
        }
      });

      this.el.addEventListener('change', e => {
        const wrap = e.target.closest('[data-cd-wrap]');
        if (wrap) {
          if (wrap.checked) this.addItem(parseInt(wrap.dataset.variantId, 10), 1, {}, true);
          else if (wrap.dataset.key) this._change(wrap.dataset.key, 0);
        }
      });

      const saveNote = debounce(async (val, status) => {
        try {
          await fetch(`${root}cart/update.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: val })
          });
          if (status) { status.textContent = 'Saved'; setTimeout(() => { status.textContent = ''; }, 1500); }
        } catch (err) {}
      }, 600);
      this.el.addEventListener('input', e => {
        const note = e.target.closest('[data-cd-note]');
        if (note) saveNote(note.value, $('[data-cd-note-status]', this.el));
      });

      this.el.addEventListener('submit', e => {
        const form = e.target.closest('[data-cd-code-form]');
        if (!form) return;
        e.preventDefault();
        const code = (form.elements.code.value || '').trim();
        if (code) this._applyCode(code);
      });
    }

    /* ── Open / close ── */
    open() {
      if (!this.el) return;
      if ((window.TeakTale || {}).cartType === 'page' && !(window.Shopify && Shopify.designMode)) {
        window.location.href = `${root}cart`; return;
      }
      if (this.el.classList.contains('is-open')) { this.refresh(); return; }
      this._stamp();
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false'); this.el.inert = false;
      $$('[data-open-cart]').forEach(b => b.setAttribute('aria-expanded', 'true'));
      window.backdrop?.show();
      this._trap();
      this._startTick();
      this._loadAiRecs();
      this.refresh();
    }
    /* What happens after any add to cart (Theme settings > Cart) */
    afterAdd() {
      const cfg = window.TeakTale || {};
      const mode = cfg.cartAfterAdd || 'drawer';
      if (mode === 'page' || (mode === 'drawer' && cfg.cartType === 'page')) { window.location.href = `${root}cart`; return; }
      if (mode === 'toast') { this._toast('Added to cart', 'success'); return; }
      this.open();
    }
    close() {
      if (!this.el?.classList.contains('is-open')) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true'); this.el.inert = true;
      $$('[data-open-cart]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      window.backdrop?.hide();
      this._untrap?.();
      clearInterval(this.tick); this.tick = null;
      this._lastFocus?.focus?.();
    }
    _trap() {
      this._lastFocus = document.activeElement;
      const handler = e => {
        if (e.key !== 'Tab') return;
        const f = $$('a[href],button:not([disabled]),input:not([disabled]),textarea,select,[tabindex]:not([tabindex="-1"])', this.el)
          .filter(n => n.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      this.el.addEventListener('keydown', handler);
      this._untrap = () => this.el.removeEventListener('keydown', handler);
      setTimeout(() => $('.cd__close', this.el)?.focus({ preventScroll: true }), 60);
    }

    /* ── Network ── */
    async _post(url, body) {
      this._setBusy(true);
      try {
        let opts;
        if (body instanceof FormData) {
          /* multipart (file uploads): the browser sets the boundary header itself */
          body.append('sections', SECTION);
          body.append('sections_url', window.location.pathname);
          opts = { method: 'POST', headers: { Accept: 'application/json' }, body };
        } else {
          opts = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname })
          };
        }
        const res = await fetch(url, opts);
        const json = await res.json();
        if (!res.ok || json.status) {
          throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
        }
        if (json.sections && json.sections[SECTION]) this._render(json.sections[SECTION]);
        else await this.refresh();
        return json;
      } finally {
        this._setBusy(false);
      }
    }
    async refresh() {
      try {
        const res = await fetch(`${root}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
        const json = await res.json();
        if (json[SECTION]) this._render(json[SECTION]);
      } catch (e) {}
    }
    async addItem(variantId, quantity = 1, properties = {}, silent = false) {
      if (!silent) this._toTop = true;
      const json = await this._post(`${root}cart/add.js`, { items: [{ id: variantId, quantity, properties }] });
      document.dispatchEvent(new CustomEvent('tt:cart:updated', { bubbles: true, detail: { ...json, _fromDrawer: true } }));
      if (!silent) window.ttTrack?.('add_to_cart', { variantId, quantity });
      return json;
    }
    /* One line sent as multipart form data (needed when a photo is attached). fd holds id, quantity, properties[...] */
    async addFormData(fd, silent = false) {
      if (!silent) this._toTop = true;
      const json = await this._post(`${root}cart/add.js`, fd);
      document.dispatchEvent(new CustomEvent('tt:cart:updated', { bubbles: true, detail: { ...json, _fromDrawer: true } }));
      if (!silent) window.ttTrack?.('add_to_cart', { variantId: fd.get('id'), quantity: fd.get('quantity') });
      return json;
    }
    /* Several lines in one request (bundles). items = [{id, quantity, properties?}] */
    async addItems(items) {
      this._toTop = true;
      const json = await this._post(`${root}cart/add.js`, { items });
      document.dispatchEvent(new CustomEvent('tt:cart:updated', { bubbles: true, detail: { ...json, _fromDrawer: true } }));
      return json;
    }
    async _change(key, quantity) {
      if (!key) return;
      const line = $(`.cd-item[data-key="${CSS.escape(key)}"]`, this.el);
      if (quantity === 0 && line) line.classList.add('is-removing');
      try {
        await this._post(`${root}cart/change.js`, { id: key, quantity });
        document.dispatchEvent(new CustomEvent('tt:cart:updated', { bubbles: true, detail: { _fromDrawer: true } }));
      } catch (err) {
        line?.classList.remove('is-removing');
        this._toast(err.message, 'error');
      }
    }

    /* ── Discount codes (Cart AJAX API `discount` param) ── */
    _appliedCodes() {
      return (this.el.dataset.appliedCodes || '').split(',').map(c => c.trim()).filter(Boolean);
    }
    async _applyCode(code) {
      const clean = code.trim();
      const codes = this._appliedCodes().filter(c => c.toLowerCase() !== clean.toLowerCase());
      codes.push(clean);
      try {
        const cart = await this._post(`${root}cart/update.js`, { discount: codes.join(',') });
        const hit = (cart.discount_codes || []).find(d => d.code.toLowerCase() === clean.toLowerCase());
        const msg = $('[data-cd-code-msg]', this.el);
        if (hit && hit.applicable) {
          if (msg) { msg.textContent = `${clean.toUpperCase()} applied`; msg.className = 'cd-coupons__msg is-ok'; }
          this.el.querySelector('.cd-coupons')?.classList.add('is-pop');
        } else if (msg) {
          msg.textContent = `${clean.toUpperCase()} isn't valid for this cart yet`;
          msg.className = 'cd-coupons__msg is-error';
        }
      } catch (err) { this._toast(err.message, 'error'); }
    }
    async _removeCode(code) {
      const codes = this._appliedCodes().filter(c => c.toLowerCase() !== code.toLowerCase());
      try { await this._post(`${root}cart/update.js`, { discount: codes.join(',') }); }
      catch (err) { this._toast(err.message, 'error'); }
    }

    /* ── Render ── */
    _render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById('CartDrawer');
      if (!next) return;

      // Remember UI state that a swap would reset
      const body = $('#CartDrawerBody', this.el);
      // Jump to the top after an add (or when leaving the empty state) so the
      // shopper sees the new line, the rewards bar and the timer first.
      const wasEmpty = !$('.cd-items', this.el);
      const scrollTop = (!body || wasEmpty || this._toTop) ? 0 : body.scrollTop;
      this._toTop = false;
      const tabs = {};
      $$('[data-cd-tabs]', this.el).forEach(r => {
        const a = $('.cd-tab.is-active', r);
        if (a) tabs[r.dataset.cdRegion] = a.dataset.cdTab;
      });
      const rails = {};
      $$('[data-cd-region]', this.el).forEach(r => {
        rails[r.dataset.cdRegion] = $$('.cd-rail', r).map(n => n.scrollLeft);
      });
      const fills = {};
      $$('.cd-rewards__fill', this.el).forEach(f => { fills[f.id] = getComputedStyle(f).width; });
      const noteFocused = document.activeElement?.matches?.('[data-cd-note]');
      const couponListOpen = !($('#CdCouponList', this.el)?.hidden ?? true);

      // Swap
      ['count', 'appliedCodes', 'cartProducts'].forEach(k => { this.el.dataset[k] = next.dataset[k] || ''; });
      $('.cd__header', this.el).replaceWith($('.cd__header', next));
      if (body) body.innerHTML = $('#CartDrawerBody', next).innerHTML;
      $('.cd__footer', this.el).replaceWith($('.cd__footer', next));

      // Restore
      if (body) body.scrollTop = scrollTop;
      Object.entries(tabs).forEach(([region, id]) => {
        const r = $(`[data-cd-region="${region}"]`, this.el);
        const btn = r && $(`[data-cd-tab="${id}"]`, r);
        if (btn) this._switchTab(btn, true);
      });
      Object.entries(rails).forEach(([region, list]) => {
        const r = $(`[data-cd-region="${region}"]`, this.el);
        if (r) $$('.cd-rail', r).forEach((n, i) => { n.scrollLeft = list[i] || 0; });
      });
      $$('.cd-rewards__fill', this.el).forEach(f => {
        if (!fills[f.id]) return;
        f.style.transition = 'none';
        f.style.width = fills[f.id];
        void f.offsetWidth;
        f.style.transition = '';
        f.style.width = '';
      });
      if (couponListOpen) {
        const l = $('#CdCouponList', this.el);
        if (l) { l.hidden = false; $('[data-cd-toggle="CdCouponList"]', this.el)?.setAttribute('aria-expanded', 'true'); }
      }
      if (noteFocused) $('[data-cd-note]', this.el)?.focus();

      this._updateCount(parseInt(this.el.dataset.count || '0', 10));
      this._afterRender();
    }
    /* Product rails are shipped inside <template> so closed drawers add no DOM weight; stamp them when shown */
    _stamp() {
      $$('template[data-cd-lazy]', this.el).forEach(t => t.replaceWith(t.content.cloneNode(true)));
      this._hideInCart?.();
    }
    _afterRender() {
      if (this.el.classList.contains('is-open')) this._stamp();
      this._runTimers();
      if (this.el.classList.contains('is-open')) this._loadAiRecs();
      this._hideInCart();
    }
    _updateCount(n) {
      $$('[data-cart-count]').forEach(el => {
        if (el.closest('#CartDrawer')) return;
        el.textContent = n;
        el.style.display = n > 0 ? '' : 'none';
        el.hidden = n === 0;
      });
      $$('[data-open-cart]').forEach(b => {
        const base = (b.getAttribute('aria-label') || 'Cart').replace(/\s*\(\d+.*\)$/, '');
        b.setAttribute('aria-label', `${base} (${n})`);
      });
      if (window.TeakTale) window.TeakTale.cartItemCount = n;
    }
    _setBusy(on) {
      this.busy = on;
      this.el.classList.toggle('is-busy', on);
      this.el.setAttribute('aria-busy', String(on));
    }
    _toast(msg, type) {
      if (window.ttToast) window.ttToast.show(msg, type);
      else console.warn(msg);
    }

    /* ── Tabs ── */
    _switchTab(btn, instant = false) {
      const region = btn.closest('[data-cd-tabs]');
      if (!region) return;
      $$('.cd-tab', region).forEach(b => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', String(on));
      });
      $$('[data-cd-panel]', region).forEach(p => {
        const on = p.dataset.cdPanel === btn.dataset.cdTab;
        p.hidden = !on;
        if (on && !instant) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
      });
      btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: instant ? 'auto' : 'smooth' });
    }

    /* ── AI recommendations (loaded only once the drawer is open) ── */
    _loadAiRecs() {
      $$('[data-cd-ai-recs]', this.el).forEach(async rail => {
        const pid = rail.dataset.productId;
        if (!pid || rail.dataset.loaded) return;
        rail.dataset.loaded = '1';
        const key = `${pid}|${rail.dataset.intent}|${rail.dataset.limit}`;
        let html = this.recCache.get(key);
        if (html === undefined) {
          try {
            const url = `${root}recommendations/products?product_id=${pid}&limit=${rail.dataset.limit}&intent=${rail.dataset.intent}&section_id=cart-recommendations`;
            const text = await (await fetch(url)).text();
            const d = new DOMParser().parseFromString(text, 'text/html');
            html = d.querySelector('.cd-rail-src')?.innerHTML.trim() || '';
          } catch (e) { html = ''; }
          this.recCache.set(key, html);
        }
        rail.innerHTML = html;
        this._hideInCart();
        const panel = rail.closest('[data-cd-panel]');
        const region = rail.closest('[data-cd-tabs]');
        if (!rail.children.length && panel && region) {
          // Nothing to recommend: hide this tab, fall back to the next one
          const tab = $(`[data-cd-tab="${panel.dataset.cdPanel}"]`, region);
          const wasActive = tab ? tab.classList.contains('is-active') : !panel.hidden;
          tab?.remove(); panel.remove();
          const nextTab = $('.cd-tab', region);
          if (wasActive && nextTab) this._switchTab(nextTab, true);
          else if (wasActive) { const p = $('[data-cd-panel]', region); if (p) p.hidden = false; }
          if (!$('[data-cd-panel]', region)) region.hidden = true;
          if ($$('.cd-tab', region).length < 2) $('.cd-tabs', region)?.remove();
        }
      });
    }
    _hideInCart() {
      const ids = new Set((this.el.dataset.cartProducts || '').split(',').filter(Boolean));
      $$('[data-cd-ai-recs] .cd-rec', this.el).forEach(c => { c.hidden = ids.has(c.dataset.productId); });
    }

    /* ── Reservation timer + deal countdowns ── */
    _runTimers() {
      const timer = $('[data-cd-timer]', this.el);
      if (timer && !store.get(TIMER_KEY) && this.el.classList.contains('is-open')) {
        store.set(TIMER_KEY, String(Date.now() + parseInt(timer.dataset.minutes, 10) * 60000));
      }
      this._paintTimers();
      if (this.el.classList.contains('is-open')) this._startTick();
    }
    _startTick() {
      if (this.tick) return;
      const timer = $('[data-cd-timer]', this.el);
      if (timer && !store.get(TIMER_KEY)) store.set(TIMER_KEY, String(Date.now() + parseInt(timer.dataset.minutes, 10) * 60000));
      this._paintTimers();
      this.tick = setInterval(() => this._paintTimers(), 1000);
    }
    _timerEnd(timer) {
      const mode = timer.dataset.mode || 'session';
      if (mode === 'fixed') return new Date((timer.dataset.end || '').trim().replace(' ', 'T')).getTime() || 0;
      if (mode === 'daily') { const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime(); }
      return parseInt(store.get(TIMER_KEY) || '0', 10);
    }
    _paintTimers() {
      const timer = $('[data-cd-timer]', this.el);
      if (timer) {
        const mode = timer.dataset.mode || 'session';
        let end = this._timerEnd(timer);
        let left = end - Date.now();
        if (end && left <= 0) {
          if (mode === 'session' && timer.dataset.expire === 'restart') {
            end = Date.now() + parseInt(timer.dataset.minutes, 10) * 60000;
            store.set(TIMER_KEY, String(end));
            left = end - Date.now();
          } else if (timer.dataset.expiredText) {
            const txt = $('.cd-timer__text', timer), clock = $('.cd-timer__clock', timer);
            if (txt) txt.textContent = timer.dataset.expiredText;
            if (clock) clock.hidden = true;
          } else { timer.hidden = true; }
        }
        if (left > 0) {
          const h = Math.floor(left / 36e5), m = Math.floor((left % 36e5) / 60000), s = Math.floor((left % 60000) / 1000);
          const hh = $('[data-cd-hh]', timer), mm = $('[data-cd-mm]', timer), ss = $('[data-cd-ss]', timer), sep = $('[data-cd-hsep]', timer);
          if (hh) { hh.hidden = h === 0; hh.textContent = `${pad(h)}h`; }
          if (sep) sep.hidden = h === 0;
          if (mm) mm.textContent = `${pad(m)}m`;
          if (ss) ss.textContent = `${pad(s)}s`;
          timer.classList.toggle('is-urgent', left < 120000);
        }
      }
      $$('[data-cd-countdown]', this.el).forEach(el => {
        const end = new Date(el.dataset.cdCountdown.trim().replace(' ', 'T')).getTime();
        const out = $('[data-cd-countdown-text]', el);
        const left = end - Date.now();
        if (!end || left <= 0) { el.hidden = true; return; }
        const d = Math.floor(left / 864e5), h = Math.floor((left % 864e5) / 36e5),
              m = Math.floor((left % 36e5) / 6e4), s = Math.floor((left % 6e4) / 1e3);
        if (out) out.textContent = `${d ? d + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
      });
    }
  }

  window.TTCartDrawer = TTCartDrawer;
})();
